/**
 * Pickup visuals (FS-07 §2.6). Three channels, none of them hue:
 *
 *  1. **Silhouette** — a closed convex solid (an octahedron). Every hazard kind is an open surface,
 *     a ribbon, a ring or a slab, so in greyscale a pickup cannot be confused for one (R34). The
 *     node glyph gave up this shape when it became a bracket pair, precisely so that the diamond
 *     means one thing.
 *  2. **Motion** — spins on its own axis at `PICKUP_IDLE_SPIN`, independent of scroll. Nothing else
 *     in the playfield spins in place. Cosmetic only; it never feeds simulation state.
 *  3. **Affordance** — a ring drawn at exactly `PICKUP_RADIUS`, so what is drawn is what collects
 *     (R35), plus a lead-in line from the hull inside `LURE_RANGE`.
 *
 * The first pickup a profile ever sees gets the emphasis treatment for its whole approach (R36).
 */
import * as THREE from 'three';
import { LURE_RANGE, PICKUP_CAP, PICKUP_IDLE_SPIN, PICKUP_RADIUS, type Pickup } from '../game/pickups';
import { PALETTE } from './palette';

const RING_SEGMENTS = 40;

class PickupVisual {
  readonly group = new THREE.Group();
  private body: THREE.Mesh;
  private ring: THREE.LineLoop;
  private lure: THREE.Line;
  private lurePositions = new Float32Array(6);

  constructor() {
    // 1. The closed convex solid. Wireframe over a solid core so it reads at depth without
    //    becoming a bright blob near the camera.
    const geo = new THREE.OctahedronGeometry(0.55);
    this.body = new THREE.Mesh(
      geo,
      new THREE.MeshBasicMaterial({ color: PALETTE.accent, transparent: true, opacity: 0.9 }),
    );
    this.group.add(this.body);

    // 3. The affordance ring — PICKUP_RADIUS exactly. AC-32 asserts the drawn radius matches the
    //    collecting radius to within 2 px, so this must never be a "looks about right" number.
    const ringPts: number[] = [];
    for (let i = 0; i <= RING_SEGMENTS; i++) {
      const a = (i / RING_SEGMENTS) * Math.PI * 2;
      ringPts.push(Math.cos(a) * PICKUP_RADIUS, Math.sin(a) * PICKUP_RADIUS, 0);
    }
    const ringGeo = new THREE.BufferGeometry();
    ringGeo.setAttribute('position', new THREE.Float32BufferAttribute(ringPts, 3));
    this.ring = new THREE.LineLoop(
      ringGeo,
      new THREE.LineBasicMaterial({ color: PALETTE.accent, transparent: true, opacity: 0.5 }),
    );
    this.group.add(this.ring);

    // The lead-in line lives in world space, not in the pickup's group, because it spans from the
    // hull to the pickup.
    const lureGeo = new THREE.BufferGeometry();
    lureGeo.setAttribute('position', new THREE.BufferAttribute(this.lurePositions, 3));
    this.lure = new THREE.Line(
      lureGeo,
      new THREE.LineBasicMaterial({ color: PALETTE.accent, transparent: true, opacity: 0.35 }),
    );
    this.group.add(this.lure);
  }

  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  update(p: Pickup, alpha: number, dt: number, shipX: number, shipY: number): void {
    const z = p.prevZ + (p.z - p.prevZ) * alpha;
    const x = Math.sin(p.theta) * p.r;
    const y = Math.cos(p.theta) * p.r;
    this.group.position.set(x, y, z);

    // Channel 2: the idle spin. Cosmetic — `p.spin` is never read by the simulation.
    p.spin += dt * PICKUP_IDLE_SPIN * Math.PI * 2;
    this.body.rotation.y = p.spin;
    this.body.rotation.x = p.spin * 0.5;

    // Emphasis: the first pickup a profile ever meets is lit from spawn (R36); everything else
    // fades in as it approaches, like a hazard telegraph.
    const approach = Math.min(1, Math.max(0, (z + 240) / 120));
    const emphasis = p.firstEver ? 1 : approach;

    (this.body.material as THREE.MeshBasicMaterial).opacity = 0.35 + 0.6 * emphasis;
    (this.ring.material as THREE.LineBasicMaterial).opacity = 0.35 + 0.55 * emphasis;
    // The ring faces the player, so its projected radius is the collection radius.
    this.ring.rotation.set(0, 0, 0);

    // The lead-in line, inside LURE_RANGE. It marks the pickup — it is explicitly NOT a promised
    // safe path (FS-07 F21); Echo Pilot owns that, and this line is dashed-thin and low-opacity so
    // the two never read alike.
    const dz = -z; // distance ahead of the ship plane
    const inRange = dz > 0 && dz < LURE_RANGE;
    this.lure.visible = inRange || p.firstEver;
    if (this.lure.visible) {
      // Local space: from the pickup back to the hull's XY at the ship plane.
      this.lurePositions[0] = 0;
      this.lurePositions[1] = 0;
      this.lurePositions[2] = 0;
      this.lurePositions[3] = shipX - x;
      this.lurePositions[4] = shipY - y;
      this.lurePositions[5] = -z;
      this.lure.geometry.attributes.position.needsUpdate = true;
      (this.lure.material as THREE.LineBasicMaterial).opacity = (p.firstEver ? 0.5 : 0.3) * (inRange ? 1 : 0.4);
    }
  }
}

export class PickupRenderer {
  readonly group = new THREE.Group();
  private pool: PickupVisual[] = [];

  constructor() {
    for (let i = 0; i < PICKUP_CAP; i++) {
      const v = new PickupVisual();
      v.setVisible(false);
      this.pool.push(v);
      this.group.add(v.group);
    }
  }

  sync(live: readonly Pickup[], liveCount: number, alpha: number, dt: number, shipX: number, shipY: number): void {
    for (let i = 0; i < this.pool.length; i++) {
      const v = this.pool[i]!;
      if (i < liveCount) {
        v.setVisible(true);
        v.update(live[i]!, alpha, dt, shipX, shipY);
      } else {
        v.setVisible(false);
      }
    }
  }
}
